import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, switchMap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { daysFromNow } from 'src/app/common/app.helpers';
import {
  ActivityLogEventView,
  EventBodyView,
  FilesActivityLogView,
  RequestDataActivityLog,
  UsersWithDescriptorView,
} from 'src/app/interfaces/activity.interface';
import {
  ActivityLogEvent,
  FileActivityLog,
  FilesActivityLog,
  FoldersResponseInner,
  ListOfBasicUsers,
  DefaultService as RestApiService,
  UserBasic,
  UserPermissionDifferences,
} from '../../swagger-api';

@Injectable({ providedIn: 'root' })
export class ActivitiesService {
  private listUsers: ListOfBasicUsers = { items: [] };

  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);

  public showIndividualFileActivities(
    items: FoldersResponseInner[],
    data?: RequestDataActivityLog,
  ): Observable<FilesActivityLogView> {
    return this.getActivitiesById(items[0].entity.entity_id, data.offset, data.limit)
      .pipe(
        switchMap((response: FileActivityLog): Observable<FileActivityLog | FilesActivityLog> => {
          return this.getSharedUsersInfo(response);
        }),
        switchMap((response: FileActivityLog | FilesActivityLog): Observable<FilesActivityLogView> => {
          return this.makeDescription(response as FilesActivityLogView, this.listUsers.items);
        }),
      )
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }

  public getAllActivities(data: RequestDataActivityLog): Observable<FilesActivityLogView> {
    return this.apiService
      .restFileStorageFilesActivityLogIndex(data.offset, data.limit, data.sorting)
      .pipe(
        switchMap((response: FilesActivityLog): Observable<FilesActivityLog> => {
          return this.getSharedUsersInfo(response);
        }),
        switchMap((response: FilesActivityLog): Observable<FilesActivityLogView> => {
          return this.makeDescription(response as FilesActivityLogView, this.listUsers.items);
        }),
      )
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }

  private makeDescription(res: FilesActivityLogView, users: UserBasic[]): Observable<FilesActivityLogView> {
    const filesActivityLog: FilesActivityLogView = structuredClone(res);
    filesActivityLog.items.map((item: ActivityLogEventView) => {
      item.event_payload.file_view.ownerItem = users.find(
        ({ user_id: nId }: UserBasic) => item.event_payload.event_body.actor_account_id === nId,
      );
      item.event_payload.file_view.activeUserId = item.event_payload.event_body.actor_account_id;
      item.event_payload.file_view.event = item.event_type;
      item.event_payload.file_view.daysFromNow = daysFromNow(item.event_datetime);

      let userIdForItem = '';
      let usedUser: UserBasic = null;
      let usedUsers: UserBasic[] = [];

      const { actor_account_id, related_account_ids } = item.event_payload.event_body;

      if (related_account_ids) {
        usedUsers = users.filter(({ user_id: uId }: UserBasic) => related_account_ids.some((id: string) => id === uId));
      } else {
        userIdForItem = actor_account_id;
      }

      const permission_changes: UserPermissionDifferences[] = item.event_payload.event_body.permission_changes;
      const usersWithDescriptor: UsersWithDescriptorView[] = [];

      switch (item.event_type) {
        case ActivityLogEvent.EventTypeEnum.FilesUploadedEvent:
          usedUser = users.find((user: UserBasic) => user.user_id === actor_account_id);

          item.event_payload.file_view.user = usedUser;
          return item;

        case ActivityLogEvent.EventTypeEnum.FileShared:
          item.event_payload.file_view.users = this.sortArrayOfUser(usedUsers) as UserBasic[];
          return item;

        case ActivityLogEvent.EventTypeEnum.FileUnshared:
          item.event_payload.file_view.users = this.sortArrayOfUser(usedUsers) as UserBasic[];
          return item;

        case ActivityLogEvent.EventTypeEnum.FilePermissionUpdated:
          permission_changes.map((permission: UserPermissionDifferences) => {
            const user: UserBasic = users.find(
              (searchUser: UserBasic) => searchUser.user_id === permission.related_account_id,
            );
            usersWithDescriptor.push({
              displayed_name: user.displayed_name,
              permission: permission.permission_differences.resulting_permission,
              user_id: user.user_id,
              account_status: user.account_status,
              reaction: user.reaction,
            });
          });
          item.event_payload.file_view.usersWithDescriptor = this.sortArrayOfUser(
            usersWithDescriptor,
          ) as UsersWithDescriptorView[];
          return item;

        case ActivityLogEvent.EventTypeEnum.FileDownloaded:
          usedUser = users.find((user: UserBasic) => user.user_id === userIdForItem);
          item.event_payload.file_view.user = usedUser;
          return item;

        case ActivityLogEvent.EventTypeEnum.FileRemoved:
          usedUser = users.find((user: UserBasic) => user.user_id === userIdForItem);
          item.event_payload.file_view.user = usedUser;
          return item;

        default:
          throw new Error(`Unsupported event type: ${item.event_type}`);
      }
    });
    return of(filesActivityLog);
  }

  private getActivitiesById(fileDescriptorId: string, offset?: number, limit?: number): Observable<FileActivityLog> {
    return this.apiService.restFileStorageFileActivityLogIndex(fileDescriptorId, offset, limit);
  }

  private sortArrayOfUser(users: (UserBasic | UsersWithDescriptorView)[]): (UserBasic | UsersWithDescriptorView)[] {
    const sortUsers = users.sort((a, b) => a.displayed_name.localeCompare(b.displayed_name));
    const index = sortUsers.findIndex((v) => v.user_id === this.sessionService.userId);

    if (index > 0) sortUsers.unshift(sortUsers.splice(index, 1)[0]);
    return sortUsers;
  }

  private getSharedUsersInfo(
    response: FileActivityLog | FilesActivityLog,
  ): Observable<FileActivityLog | FilesActivityLog> {
    const sharedUsersIds: string[] = [];
    response.items.map((item: ActivityLogEvent) => {
      const { actor_account_id, ...rest } = item.event_payload.event_body as EventBodyView;

      if (rest.related_account_ids) {
        sharedUsersIds.push(...rest.related_account_ids);
      } else if (rest.permission_changes) {
        rest.permission_changes.map((permission: UserPermissionDifferences) => {
          sharedUsersIds.push(permission.related_account_id);
        });
      }

      sharedUsersIds.push(actor_account_id);
    });

    const uniqueUsersIds: string[] = [...new Set(sharedUsersIds)];
    if (uniqueUsersIds.length > 0) {
      return this.apiService.restUserIndex(uniqueUsersIds).pipe(
        map((result: ListOfBasicUsers) => {
          this.listUsers = result;
          return response;
        }),
        catchError(() => EMPTY),
      );
    }

    return of(response);
  }
}
